import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Users, Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCreateAlbum } from '@/hooks/useAlbums';
import { useUserPlan } from '@/hooks/usePlan';
import { cn } from '@/lib/utils';

interface CreateAlbumDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (albumId: string) => void;
}

export function CreateAlbumDialog({ open, onOpenChange, onSuccess }: CreateAlbumDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isCollaborative, setIsCollaborative] = useState(false);
  const createAlbum = useCreateAlbum();
  const { data: userPlan } = useUserPlan();

  const canCreateCollab = userPlan?.planType === 'pro' || userPlan?.planType === 'ultimate';

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setName('');
      setDescription('');
      setIsCollaborative(false);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('アルバム名は必須です');
      return;
    }

    try {
      const album = await createAlbum.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        isCollaborative: isCollaborative,
      });

      toast.success('アルバムを作成しました');
      setName('');
      setDescription('');
      setIsCollaborative(false);
      onOpenChange(false);
      onSuccess?.(album.id);
    } catch {
      toast.error('アルバムの作成に失敗しました');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>新規アルバム作成</DialogTitle>
            <DialogDescription>写真を整理するための新しいアルバムを作成します。</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="name" className="text-sm font-medium">
                名前
              </label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="マイアルバム"
                maxLength={100}
                autoFocus
              />
            </div>
            <div className="grid gap-2">
              <label htmlFor="description" className="text-sm font-medium">
                説明（任意）
              </label>
              <Input
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="お気に入りの写真コレクション"
                maxLength={500}
              />
            </div>

            {/* Collaborative Album Toggle */}
            <div className="grid gap-2">
              <div
                className={cn(
                  'flex items-start gap-3 p-3 rounded-lg border transition-colors',
                  canCreateCollab
                    ? 'border-border bg-muted/50 cursor-pointer hover:bg-muted/80'
                    : 'border-border bg-muted/30 opacity-60 cursor-not-allowed'
                )}
                onClick={() => canCreateCollab && setIsCollaborative(!isCollaborative)}
              >
                <input
                  type="checkbox"
                  id="collaborative"
                  checked={isCollaborative}
                  onChange={(e) => setIsCollaborative(e.target.checked)}
                  disabled={!canCreateCollab}
                  className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
                />
                <div className="flex-1">
                  <label
                    htmlFor="collaborative"
                    className={cn(
                      'text-sm font-medium flex items-center gap-2',
                      canCreateCollab ? 'cursor-pointer' : 'cursor-not-allowed'
                    )}
                  >
                    <Users className="h-4 w-4" />
                    コラボレーションアルバムとして作成
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    他のユーザーが参加して写真を追加できるようになります
                  </p>
                  {!canCreateCollab && (
                    <div className="mt-2 flex items-start gap-1 text-xs text-warning">
                      <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      <span>コラボレーションアルバムにはPro+プランが必要です</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              キャンセル
            </Button>
            <Button type="submit" disabled={createAlbum.isPending || !name.trim()}>
              {createAlbum.isPending ? '作成中...' : 'アルバムを作成'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
